import numeral from 'numeral'
import { useMemo } from 'react'

import { DefaultProjectImage } from 'src/components/atoms/DefaultProjectImage'
import { InternalLink } from 'src/components/atoms/Link'
import TypographyRebrand from 'src/components/atoms/TypographyRebrand/TypographyRebrand'

import { difference } from 'src/utils/date'

export interface RegistryCardRebrandProps {
  name: string
  href: string
  imageUrl?: string
  trees: number
  gifts: number
  closesOn?: string | null
  className?: string
}

const CARD_HEIGHT_CLASSES = 'h-[438px] lg:h-[544px]'

const StatBadge = ({ value, label }: { value: string; label: string }) => (
  <div className='flex items-baseline gap-1.5 text-limeFresh whitespace-nowrap'>
    <span className='font-body-rebrand text-base lg:text-[22px] font-bold leading-[1.2]'>
      {value}
    </span>
    <span className='font-body-rebrand text-[12px] leading-[1.6]'>{label}</span>
  </div>
)

const CardBody = ({
  name,
  imageUrl,
  trees,
  gifts,
  closesOn
}: Omit<RegistryCardRebrandProps, 'href' | 'className'>) => {
  const daysLeft = useMemo(
    () => (closesOn ? difference({ date1: closesOn, isUtc: true }) : null),
    [closesOn]
  )

  return (
    <div className='flex flex-col gap-3'>
      <div className={`relative ${CARD_HEIGHT_CLASSES} overflow-hidden`}>
        {imageUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={imageUrl}
            alt={name}
            className='absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105'
            loading='lazy'
          />
        ) : (
          <DefaultProjectImage
            className='absolute inset-0 w-full h-full'
            alt={name}
          />
        )}
        <div className='absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-b from-transparent to-black/80 pointer-events-none' />
        <div className='absolute bottom-6 left-6 right-6 flex items-baseline justify-between gap-4 translate-y-1.5 opacity-90 transition-all duration-500 ease-out group-hover:translate-y-0 group-hover:opacity-100'>
          <StatBadge
            value={numeral(trees).format('0,0')}
            label='trees gifted'
          />
          {daysLeft ? (
            <StatBadge
              value={numeral(daysLeft).format('0,0')}
              label='days left'
            />
          ) : (
            <StatBadge value={numeral(gifts).format('0,0')} label='gifters' />
          )}
        </div>
      </div>
      <TypographyRebrand variant='h5' color='espresso'>
        {name}
      </TypographyRebrand>
    </div>
  )
}

export const RegistryCardRebrand = ({
  name,
  href,
  imageUrl,
  trees,
  gifts,
  closesOn,
  className = ''
}: RegistryCardRebrandProps) => (
  <InternalLink
    color='black'
    href={href}
    className={`block group ${className}`}
  >
    <CardBody
      name={name}
      imageUrl={imageUrl}
      trees={trees}
      gifts={gifts}
      closesOn={closesOn}
    />
  </InternalLink>
)
